import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'

import type { InvoiceOrEstimate } from '~/types'

import type {
  PaymentRequestDialogContextType,
  PaymentRequestDialogStateType,
} from '../constants/paymentRequestDialogV2Constants'

const PaymentRequestDialogContext =
  createContext<PaymentRequestDialogContextType | null>(null)

const initialDialogState: PaymentRequestDialogStateType = {
  clinicNotes: '',
  deliveryMethod: 'email',
  sendTo: [],
}

export interface PaymentRequestDialogProviderProps {
  children: ReactNode
  invoice: InvoiceOrEstimate
}

export const PaymentRequestDialogProvider = ({
  children,
  invoice,
}: PaymentRequestDialogProviderProps) => {
  const [dialogState, setDialogState] =
    useState<PaymentRequestDialogStateType>(initialDialogState)

  const actions = useMemo(
    () => ({
      sendPayment: () => {
        // TODO: wire to API when form is ready
      },
      setClinicNotes: (clinicNotes: string) => {
        setDialogState((prev) => ({ ...prev, clinicNotes }))
      },
      setDeliveryMethod: (
        method: PaymentRequestDialogStateType['deliveryMethod'],
      ) => {
        setDialogState((prev) => ({ ...prev, deliveryMethod: method }))
      },
      setSendTo: (recipients: PaymentRequestDialogStateType['sendTo']) => {
        setDialogState((prev) => ({ ...prev, sendTo: recipients }))
      },
    }),
    [],
  )

  const contextValue = useMemo<PaymentRequestDialogContextType>(
    () => ({
      actions,
      dialogState,
      invoice,
    }),
    [actions, dialogState, invoice],
  )

  return (
    <PaymentRequestDialogContext.Provider value={contextValue}>
      {children}
    </PaymentRequestDialogContext.Provider>
  )
}

export const usePaymentRequestDialog = (): PaymentRequestDialogContextType => {
  const context = useContext(PaymentRequestDialogContext)
  if (!context) {
    throw new Error(
      'usePaymentRequestDialog must be used within a PaymentRequestDialogProvider',
    )
  }
  return context
}
